import { z } from 'zod';

// Launch gate (setter spec §10.1 checklist, §10.2 dual approval, §10.3 Level A/B change control, §13.2 go-live
// criteria). A project attends real prospects only while `live`; test contacts and the workspace allowlist keep working
// in every state so the pre-production tests can run.

const Id = z.string().min(1).max(64);
const Iso = z.string();

export const PROJECT_ACTIVATION_STATES = ['setup', 'ready_for_approval', 'live', 'paused'] as const;
export const ProjectActivationStateSchema = z.enum(PROJECT_ACTIVATION_STATES);
export type ProjectActivationState = z.infer<typeof ProjectActivationStateSchema>;
export const PROJECT_ACTIVATION_STATE_LABELS: Record<ProjectActivationState, string> = {
  setup: 'En preparación',
  ready_for_approval: 'Esperando aprobación',
  live: 'En producción',
  paused: 'Pausado',
};

export const LAUNCH_APPROVER_ROLES = ['commercial', 'technical'] as const;
export const LaunchApproverRoleSchema = z.enum(LAUNCH_APPROVER_ROLES);
export type LaunchApproverRole = z.infer<typeof LaunchApproverRoleSchema>;
export const LAUNCH_APPROVER_ROLE_LABELS: Record<LaunchApproverRole, string> = { commercial: 'Comercial', technical: 'Técnica' };

// ───────────── Checklist catalog (§10.1 sections + §13.2 criteria) ─────────────

export const LAUNCH_SECTIONS = ['commercial_info', 'commercial_config', 'team', 'integrations', 'flows_controls', 'tests', 'go_live_criteria'] as const;
export type LaunchSection = (typeof LAUNCH_SECTIONS)[number];
export const LAUNCH_SECTION_LABELS: Record<LaunchSection, string> = {
  commercial_info: 'Información comercial vigente',
  commercial_config: 'Configuración comercial',
  team: 'Equipo humano',
  integrations: 'Integraciones operativas',
  flows_controls: 'Flujos y controles',
  tests: 'Pruebas previas a producción',
  go_live_criteria: 'Criterios obligatorios de salida (§13.2)',
};

export interface LaunchItemDef {
  key: string;
  section: LaunchSection;
  label: string;
  /** auto = verified by the system from its own data; manual = a person marks it with evidence. */
  kind: 'auto' | 'manual';
  /** Whether «No aplica» (with a justification) is accepted. */
  allowNotApplicable: boolean;
  hint?: string;
}

export const LAUNCH_ITEMS = [
  { key: 'knowledge_published', section: 'commercial_info', kind: 'auto', allowNotApplicable: false, label: 'Conocimiento del proyecto publicado' },
  { key: 'inventory', section: 'commercial_info', kind: 'auto', allowNotApplicable: true, label: 'Inventario y precios autorizados cargados' },
  { key: 'official_source', section: 'commercial_info', kind: 'manual', allowNotApplicable: false, label: 'Fuente oficial y responsable de mantenerla identificados', hint: 'Por ejemplo, la carpeta de Drive del desarrollador y quién la actualiza.' },
  { key: 'qualification_criteria', section: 'commercial_config', kind: 'auto', allowNotApplicable: false, label: 'Criterios de calificación configurados' },
  { key: 'agent_published', section: 'commercial_config', kind: 'auto', allowNotApplicable: false, label: 'Configuración del agente publicada' },
  { key: 'approved_messages', section: 'commercial_config', kind: 'manual', allowNotApplicable: false, label: 'Mensajes, objeciones y alternativas autorizadas revisados' },
  { key: 'advisors', section: 'team', kind: 'auto', allowNotApplicable: false, label: 'Asesores habilitados para el proyecto' },
  { key: 'calendars_synced', section: 'team', kind: 'manual', allowNotApplicable: false, label: 'Calendarios reales sincronizados por sus responsables' },
  { key: 'escalation_owner', section: 'team', kind: 'manual', allowNotApplicable: false, label: 'Receptor humano de escalamiento e incidencias designado', hint: 'Responsable de incidencias y procedimiento ante conflictos o cambios.' },
  { key: 'crm', section: 'integrations', kind: 'auto', allowNotApplicable: true, label: 'CRM conectado y con mapeo de etapas' },
  { key: 'whatsapp_number', section: 'integrations', kind: 'auto', allowNotApplicable: false, label: 'Número de WhatsApp del proyecto con envío comprobado' },
  { key: 'calendly_event_type', section: 'integrations', kind: 'auto', allowNotApplicable: false, label: 'Tipo de evento de Calendly del proyecto' },
  { key: 'telephony', section: 'integrations', kind: 'manual', allowNotApplicable: true, label: 'Telefonía probada (si el proyecto usa llamadas)' },
  {
    key: 'media_language',
    section: 'integrations',
    kind: 'auto',
    allowNotApplicable: true,
    label: 'Medios e idioma disponibles (notas de voz, imágenes y PDF, inglés)',
    hint: 'Si un componente no se anuncia en el piloto, márcalo «No aplica» con la justificación («no activado»).',
  },
  { key: 'contact_window', section: 'flows_controls', kind: 'auto', allowNotApplicable: false, label: 'Horario de operación y ventana de contacto' },
  { key: 'cost_limits', section: 'flows_controls', kind: 'auto', allowNotApplicable: false, label: 'Límites de costos de IA configurados' },
  { key: 'flows_reviewed', section: 'flows_controls', kind: 'manual', allowNotApplicable: false, label: 'Entrada de leads, deduplicación, derivación humana y pérdidas revisadas' },
  { key: 'test_whatsapp', section: 'tests', kind: 'manual', allowNotApplicable: false, label: 'Conversaciones de WhatsApp (diurnas y nocturnas)' },
  { key: 'test_calls', section: 'tests', kind: 'manual', allowNotApplicable: true, label: 'Llamadas entrantes y salientes' },
  { key: 'test_booking', section: 'tests', kind: 'manual', allowNotApplicable: false, label: 'Citas: reserva, asesor asignado, no show y cancelación' },
  { key: 'test_context', section: 'tests', kind: 'manual', allowNotApplicable: false, label: 'Recuperación de contexto entre canales' },
  { key: 'test_failures_abuse', section: 'tests', kind: 'manual', allowNotApplicable: false, label: 'Fallos técnicos y casos de abuso' },
  { key: 'p0_pass', section: 'go_live_criteria', kind: 'manual', allowNotApplicable: false, label: 'Todas las pruebas P0 aplicables pasan, sin fallas críticas abiertas' },
  { key: 'sla_measured', section: 'go_live_criteria', kind: 'manual', allowNotApplicable: false, label: 'SLA del primer intento (menos de 5 minutos) medido y documentado' },
  { key: 'no_uncontrolled_risks', section: 'go_live_criteria', kind: 'manual', allowNotApplicable: false, label: 'Sin riesgos sin controlar: cita falsa, doble reserva, contacto no autorizado, cruce de datos, reactivación o gasto sin límite' },
  { key: 'owners_defined', section: 'go_live_criteria', kind: 'manual', allowNotApplicable: false, label: 'Responsables de inventario, calendarios, resultados de citas e incidencias definidos' },
  { key: 'developer_confirmation', section: 'go_live_criteria', kind: 'manual', allowNotApplicable: false, label: 'El desarrollador confirmó por escrito que entregó información vigente' },
  { key: 'stop_recover', section: 'go_live_criteria', kind: 'manual', allowNotApplicable: false, label: 'Mecanismo para detener y recuperar la operación probado' },
] as const satisfies readonly LaunchItemDef[];

export type LaunchItemKey = (typeof LAUNCH_ITEMS)[number]['key'];
export const LAUNCH_ITEM_KEYS = LAUNCH_ITEMS.map((i) => i.key) as [LaunchItemKey, ...LaunchItemKey[]];
export const LaunchItemKeySchema = z.enum(LAUNCH_ITEM_KEYS);

export const LAUNCH_ITEM_STATUSES = ['done', 'pending', 'not_applicable'] as const;
export const LaunchItemStatusSchema = z.enum(LAUNCH_ITEM_STATUSES);
export type LaunchItemStatus = z.infer<typeof LaunchItemStatusSchema>;
export const LAUNCH_ITEM_STATUS_LABELS: Record<LaunchItemStatus, string> = { done: 'Listo', pending: 'Pendiente', not_applicable: 'No aplica' };

const PersonRefSchema = z.object({ id: Id, name: z.string() });

export const LaunchItemSchema = z.object({
  key: LaunchItemKeySchema,
  label: z.string(),
  kind: z.enum(['auto', 'manual']),
  status: LaunchItemStatusSchema,
  /** Auto items: what the system found (or what is missing). */
  detail: z.string().nullable(),
  hint: z.string().nullable(),
  allowNotApplicable: z.boolean(),
  /** Manual items (and «No aplica» on auto items): evidence and who/when. */
  note: z.string().nullable(),
  link: z.string().nullable(),
  updatedBy: PersonRefSchema.nullable(),
  updatedAt: Iso.nullable(),
});
export type LaunchItem = z.infer<typeof LaunchItemSchema>;

export const LaunchSectionSchema = z.object({ key: z.enum(LAUNCH_SECTIONS), label: z.string(), items: z.array(LaunchItemSchema) });

export const UpdateLaunchItemSchema = z
  .object({
    status: LaunchItemStatusSchema,
    note: z.string().trim().max(2000).nullable().optional(),
    link: z.string().trim().url().max(1000).nullable().optional(),
  })
  .strict();
export type UpdateLaunchItem = z.infer<typeof UpdateLaunchItemSchema>;

// ───────────── Approvals ─────────────

export const LaunchApprovalSchema = z.object({
  id: Id,
  round: z.number().int(),
  role: LaunchApproverRoleSchema,
  user: PersonRefSchema,
  configHash: z.string(),
  note: z.string().nullable(),
  plannedActivationAt: Iso.nullable(),
  /** Still counts for its round (not withdrawn, configuration unchanged). */
  valid: z.boolean(),
  invalidReason: z.string().nullable(),
  /** What the approver saw: prompt / knowledge / configuration versions. */
  snapshot: z.record(z.string(), z.unknown()),
  createdAt: Iso,
});
export type LaunchApproval = z.infer<typeof LaunchApprovalSchema>;

export const ApproveLaunchSchema = z
  .object({
    role: LaunchApproverRoleSchema,
    note: z.string().trim().max(2000).nullable().optional(),
    plannedActivationAt: z.iso.datetime({ offset: true }).nullable().optional(),
    /** The configuration version the approver reviewed; refused when the project changed since. */
    configHash: z.string().min(1).max(128),
  })
  .strict();

export const LaunchReasonSchema = z.object({ reason: z.string().trim().min(3).max(1000) }).strict();

export const LaunchApproverSlotSchema = z.object({
  role: LaunchApproverRoleSchema,
  /** Designated user (null = default rule). */
  designated: PersonRefSchema.nullable(),
  /** Who can approve this role right now. */
  eligible: z.array(PersonRefSchema),
  defaultRule: z.string(),
});
export const LaunchApproversSchema = z.object({
  slots: z.array(LaunchApproverSlotSchema),
  /** Owner opt-in: one person may give both approvals. */
  allowSingleApprover: z.boolean(),
});
export const PutLaunchApproversSchema = z
  .object({ commercialUserId: Id.nullable(), technicalUserId: Id.nullable(), allowSingleApprover: z.boolean().optional() })
  .strict();

// ───────────── Level B change requests (§10.3) ─────────────

export const LEVEL_B_CHANGE_KINDS = ['project_settings', 'qualification_config', 'appointment_config', 'schedule', 'agent_publish', 'usage_budget', 'routine'] as const;
export const LevelBChangeKindSchema = z.enum(LEVEL_B_CHANGE_KINDS);
export type LevelBChangeKind = z.infer<typeof LevelBChangeKindSchema>;
export const LEVEL_B_CHANGE_KIND_LABELS: Record<LevelBChangeKind, string> = {
  project_settings: 'Ventana de contacto, respuestas nocturnas o frecuencia de contacto',
  qualification_config: 'Criterios de calificación',
  appointment_config: 'Recordatorios y condiciones de citas',
  schedule: 'Horario de operación de la IA',
  agent_publish: 'Publicación del prompt del proyecto',
  usage_budget: 'Límites de costos',
  routine: 'Rutinas y cadencias',
};

export const CHANGE_REQUEST_STATES = ['pending', 'applied', 'rejected', 'failed'] as const;
export const ChangeRequestStateSchema = z.enum(CHANGE_REQUEST_STATES);
export const CHANGE_REQUEST_STATE_LABELS: Record<z.infer<typeof ChangeRequestStateSchema>, string> = {
  pending: 'Pendiente de aprobación',
  applied: 'Aplicado',
  rejected: 'Rechazado',
  failed: 'No se pudo aplicar',
};

const ApprovalMarkSchema = z.object({ user: PersonRefSchema, at: Iso });
export const ChangeRequestSchema = z.object({
  id: Id,
  projectId: Id,
  kind: LevelBChangeKindSchema,
  summary: z.string(),
  state: ChangeRequestStateSchema,
  requestedBy: PersonRefSchema,
  commercial: ApprovalMarkSchema.nullable(),
  technical: ApprovalMarkSchema.nullable(),
  decisionNote: z.string().nullable(),
  applyError: z.string().nullable(),
  decidedAt: Iso.nullable(),
  createdAt: Iso,
});
export type ChangeRequest = z.infer<typeof ChangeRequestSchema>;
export const ApproveChangeRequestSchema = z.object({ role: LaunchApproverRoleSchema, note: z.string().trim().max(2000).nullable().optional() }).strict();

/** Error code returned (409) when a Level B change on a live project became a change request instead of applying. */
export const CHANGE_REQUIRES_APPROVAL = 'change_requires_approval';

// ───────────── Project launch status ─────────────

export const ProjectLaunchSchema = z.object({
  projectId: Id,
  state: ProjectActivationStateSchema,
  /** live without a recorded dual approval (existing project at migration time): no Level B change control. */
  legacyLive: z.boolean(),
  /** Level B changes go through change requests (live after a dual approval). */
  changeControl: z.boolean(),
  activatedAt: Iso.nullable(),
  round: z.number().int(),
  sections: z.array(LaunchSectionSchema),
  /** Every item is «Listo» or «No aplica». */
  checklistComplete: z.boolean(),
  pendingItems: z.number().int(),
  /** Current configuration version (hash of the Level B configuration) and its snapshot. */
  configHash: z.string(),
  snapshot: z.record(z.string(), z.unknown()),
  approvedConfigHash: z.string().nullable(),
  /** The Level B configuration differs from what was approved (resume needs a new approval round). */
  configChangedSinceApproval: z.boolean(),
  approvals: z.array(LaunchApprovalSchema),
  /** Roles the caller can approve now. */
  canApprove: z.array(LaunchApproverRoleSchema),
  changeRequests: z.array(ChangeRequestSchema),
});
export type ProjectLaunch = z.infer<typeof ProjectLaunchSchema>;
